import React, { FC, Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import Linkify from 'react-linkify';
import classNames from 'classnames';

import { useContent } from 'hooks';
import { Events } from 'analytics';
import { getRenderingEngine } from 'utilities';
import { parseHTML } from 'helpers';
import { isTextOnly } from '../helpers';

const LARGE_TEXT_THRESHOLD = 80;
const TRUNCATE_THRESHOLD = 255;

const useStyles = makeStyles(theme => ({
  root: {
    fontWeight: theme.typography.fontWeightRegular,
    whiteSpace: 'pre-line',
    ...theme.mixins.breakWord,
    '& a': {
      textDecoration: 'none',
      color: theme.palette.primary.main,
    },
    '& p': {
      marginTop: 0,
    },
    '& blockquote': {
      borderLeft: `${theme.spacing(0.5)}px solid ${theme.palette.grey[400]}`,
      paddingLeft: theme.spacing(),
    },
  },
  clamp: theme.mixins.clamp(3),
  readMore: {
    textDecoration: 'underline',
    fontWeight: theme.typography.fontWeightBold,
  },
}));

const Body: FC = () => {
  const classes = useStyles();
  const content = useContent();
  const [expanded, setExpanded] = useState(false);
  const { t } = useTranslation();
  const { body } = content.content_object;

  if (!body) {
    return null;
  }

  const textOnly = isTextOnly(content);
  const isLargeText = textOnly && body.length < LARGE_TEXT_THRESHOLD;
  const isClamped = body.length > TRUNCATE_THRESHOLD;
  const renderingEngine = getRenderingEngine();
  const canRenderClamp = renderingEngine === 'Blink';

  const showReadMore = isClamped && canRenderClamp && !expanded;
  const variant = isLargeText ? 'h6' : 'subtitle2';

  const typographyClasses = classNames(classes.root, {
    [classes.clamp]: showReadMore,
  });

  function handleReadMore() {
    setExpanded(true);
    window?.analytics?.track(Events.PostReadMoreClicked);
  }

  return (
    <Fragment>
      <Typography component="div" className={typographyClasses} variant={variant}>
        <Linkify properties={{ target: '_blank' }}>{parseHTML(body)}</Linkify>
      </Typography>
      {showReadMore && (
        <Link
          className={classes.readMore}
          component="button"
          variant="subtitle2"
          onClick={handleReadMore}
        >
          {t('components:readMore')}
        </Link>
      )}
    </Fragment>
  );
};

export default Body;
